import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of, Subject, throwError } from "rxjs";
import { catchError, map, mergeMap, retryWhen, switchMap, takeUntil, tap } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { CommunicationService } from "src/app/core/service/Communication/communication.service";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { UserService } from "src/app/core/service/user.service";
import { AulaTO } from "src/app/data/schema/Courses/AulaTO";
import { CourseSummaryDTO } from "src/app/data/schema/Courses/CourseSummaryDTO";
import { CursoTO } from "src/app/data/schema/Courses/CursoTO";
import { QuestionFilter } from "src/app/data/schema/Courses/Enum/QuestionFilter";
import { QuestionOrder } from "src/app/data/schema/Courses/Enum/QuestionOrder";
import { ModuloTO } from "src/app/data/schema/Courses/ModuloTO";
import { PerguntaTO } from "src/app/data/schema/Courses/PerguntaTO";
import { QuestionQueryParametersDTO } from "src/app/data/schema/Courses/QuestionQueryParametersDTO";
import { ReviewTO } from "src/app/data/schema/Courses/ReviewTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { CourseSubscriptionDTO } from "src/app/data/schema/UserCourse/CourseSubscriptionDTO";
import { UserCourseProgressionDTO } from "src/app/data/schema/UserCourse/UserCourseProgressionDTO";
import { ApiBillingBillsService } from "src/app/data/service/billing/api-billing-bills.service";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { ApiCourseSubscriptionService } from "src/app/data/service/userCourses/api-course-subscription.service";
import { ApiUserCoursesService } from "src/app/data/service/userCourses/api-user-courses.service";
import { genericRetryStrategy } from "src/app/utils/genericRetryStrategy";
import { CoursesMustReviewComponent } from "../page/courses-viewer/components/courses-must-review/courses-must-review.component";

@Injectable({
    providedIn: "root"
})
export class CourseService {
    user: UsuarioTO;
    private certificateUrl: BehaviorSubject<string>;

    private loading: BehaviorSubject<boolean>;
    private mustReviewBehaviour: BehaviorSubject<boolean>;
    private courseNeedReview: BehaviorSubject<boolean>;
    private course: BehaviorSubject<CursoTO>;
    private userCourseProgression: BehaviorSubject<UserCourseProgressionDTO>;
    public courseSubscription: BehaviorSubject<CourseSubscriptionDTO>;
    private questions: BehaviorSubject<PerguntaTO[]>;
    private questionFilter: BehaviorSubject<QuestionFilter>;
    private questionOrder: BehaviorSubject<QuestionOrder>;
    private questionPageNumber: BehaviorSubject<number>;
    private hasMoreQuestions: BehaviorSubject<boolean>;
    private hasBillForCertificate: BehaviorSubject<boolean>;
    private canLoadCourse: BehaviorSubject<boolean>;
    private loadingInfo: BehaviorSubject<string>;

    private coursesForRevision: BehaviorSubject<CourseSummaryDTO[]>;

    public userRatedCourse: BehaviorSubject<boolean>;
    public tabIndex: BehaviorSubject<number>;

    private dialogRefReview: MatDialogRef<CoursesMustReviewComponent, any>;

    private unsubscribeAll = new Subject();
    constructor(
        private translate: TranslateService,
        private authService: AuthService,
        private courseService: ApiCoursesService,
        private apiUserService: ApiUserService,
        private userService: UserService,
        private userCourseService: ApiUserCoursesService,
        private courseSubscriptionService: ApiCourseSubscriptionService,
        private snackBar: SnackbarService,
        private dialog: MatDialog,
        private communicationService: CommunicationService,
        private dataAnalysisService: DataAnalysisService,
        private billsService: ApiBillingBillsService
    ) {
        this.certificateUrl = new BehaviorSubject<string>(null);
        this.loading = new BehaviorSubject<boolean>(false);
        this.mustReviewBehaviour = new BehaviorSubject<boolean>(false);
        this.userRatedCourse = new BehaviorSubject<boolean>(false);
        this.courseNeedReview = new BehaviorSubject<boolean>(false);

        this.course = new BehaviorSubject<CursoTO>(undefined);
        this.userCourseProgression = new BehaviorSubject<UserCourseProgressionDTO>(undefined);
        this.courseSubscription = new BehaviorSubject<CourseSubscriptionDTO>(undefined);
        this.canLoadCourse = new BehaviorSubject<boolean>(undefined);
        this.loadingInfo = new BehaviorSubject<string>("GETTING_COURSE");

        this.questions = new BehaviorSubject<PerguntaTO[]>(undefined);
        this.questionFilter = new BehaviorSubject<QuestionFilter>(undefined);
        this.questionOrder = new BehaviorSubject<QuestionOrder>(undefined);
        this.questionPageNumber = new BehaviorSubject<number>(1);
        this.hasMoreQuestions = new BehaviorSubject<boolean>(true);
        this.hasBillForCertificate = new BehaviorSubject<boolean>(false);
        this.tabIndex = new BehaviorSubject<number>(0);

        this.coursesForRevision = new BehaviorSubject<CourseSummaryDTO[]>(undefined);

        this.communicationService.getUpdateCourseProgression().subscribe((courseId) => {
            if (courseId && this.course.value?.id == courseId) this.getCourseAndProgressionById(courseId);
        });

        this.userService
            .getCurrentUser()
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((user) => (this.user = user));
    }

    public mustReviewAsync = (): Observable<boolean> => this.mustReviewBehaviour.asObservable();

    public hasBillForCertificate$ = (): Observable<boolean> => this.hasBillForCertificate.asObservable();

    public getLoading = (): Observable<boolean> => this.loading.asObservable();

    public getCurrentCourse = (): Observable<CursoTO> => this.course.asObservable();

    public getCurrentUserCourseProgression = (): Observable<UserCourseProgressionDTO> =>
        this.userCourseProgression.asObservable();

    public getCurrentCourseSubscription = (): Observable<CourseSubscriptionDTO> =>
        this.courseSubscription.asObservable();

    getCanLoadCourse = (): Observable<boolean> => this.canLoadCourse.asObservable();
    getLoadingInfo = (): Observable<string> => this.loadingInfo.asObservable();

    public tabIndexAsync = (): Observable<number> => this.tabIndex.asObservable();

    public getCourseAndProgressionById(courseId: string): void {
        this.loading.next(true);
        this.loadingInfo.next("GETTING_COURSE");
        this.courseService
            .getCourseById(courseId)
            .pipe(
                retryWhen(
                    genericRetryStrategy({
                        maxRetryAttempts: 10,
                        scalingDuration: 2000,
                        excludedStatusCodes: [500, 401]
                    })
                ),
                catchError((error) => of(error))
            )
            .subscribe(
                (course) => {
                    this.course.next(course);
                    this.courseNeedReview.next(["PendenteRevisao", "EmRevisao"].includes(this.course.value.situacao));

                    this.getUserCourseProgression();
                    this.loadHasBillForCertificate();
                },
                (error) => {
                    this.loading.next(false);
                    this.snackBar.showHttpMessage(error.status, error.message);
                }
            );
    }

    loadHasBillForCertificate(): void {
        if (!this.course?.value?.id) return;
        this.billsService
            .hasBillForCertificate(this.course.value.id)
            .subscribe((res) => this.hasBillForCertificate.next(res));
    }

    public refreshCourse(): void {
        this.getCourseAndProgressionById(this.course.value.id);
    }

    getUserCourseProgression(): void {
        if (!this.courseSubscription?.value?.id) return;

        this.loading.next(true);
        this.loadingInfo.next("GETTING_PROGRESSION");
        this.courseSubscriptionService
            .GetProgressionByCourseSubscription(this.courseSubscription.value.id)
            .pipe(
                mergeMap((progression) => {
                    this.setCurrentDataByProgression(progression);

                    return this.courseService.getCourseReviews(
                        this.course.value.id,
                        0,
                        10,
                        null,
                        this.authService.getClaimsUserId()
                    );
                })
            )
            .subscribe(
                (res) => {
                    if (res.length > 0) this.userRatedCourse.next(true);
                    else this.userRatedCourse.next(false);
                    this.loading.next(false);

                    if (
                        res.length <= 0 &&
                        this.userCourseProgression.value.concluded &&
                        !this.course.value.dontRequestEvaluation
                    ) {
                        if (this.dialogRefReview) this.dialogRefReview.close(false);
                        this.dialogRefReview = this.dialog.open(CoursesMustReviewComponent);
                        this.dialogRefReview.afterClosed().subscribe((res) => {
                            if (!res) return;

                            this.tabIndex.next(5);
                        });
                    }
                },
                (error) => {
                    this.loading.next(false);
                    this.snackBar.showHttpMessage(error.status, error.message);
                }
            );

        this.courseSubscriptionService
            .GetList({
                count: false,
                courseId: this.course.value.id,
                userId: this.authService.getClaimsUserId(),
                orderBy: "createdat",
                sortOrder: "desc",
                active: true,
                skip: 0,
                take: 1
            })
            .subscribe((subscription) => this.setCurrentCourseSubscription(subscription?.data[0]));
    }

    public getAureaUrl(moduleId: string, lessonId: string): Observable<string> {
        return this.courseService.GetAureaUrl(this.course.value.id, moduleId, lessonId);
    }

    review(value: number, title: string, feedback: string): void {
        this.loading.next(true);

        this.courseService.createCourseReview(this.course.value.id, value, title, feedback).subscribe(
            (response) => {
                this.translate.get("VIEWER.TABS.INFO.MESSAGES.REVIEWED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
                this.userRatedCourse.next(true);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
            },
            () => this.loading.next(false)
        );
    }

    setCurrentDataByProgression(progressao: UserCourseProgressionDTO): void {
        if (progressao) {
            this.userCourseProgression.next(progressao);
            localStorage.setItem(`${progressao.userId}_${progressao.courseId}`, JSON.stringify(progressao));

            if (progressao.urlCertificate) this.certificateUrl.next(progressao.urlCertificate);
        }
    }

    setCurrentCourseSubscription(courseSubscription: CourseSubscriptionDTO): void {
        this.courseSubscription.next(courseSubscription);
    }

    public clear(): void {
        this.loading.next(false);
        this.course.next(undefined);
        this.userCourseProgression.next(undefined);
        this.courseSubscription.next(undefined);

        this.questions.next(undefined);
        this.questionFilter.next(undefined);
        this.questionOrder.next(undefined);
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);

        this.coursesForRevision.next([]);
        this.unsubscribeAll.unsubscribe();
    }

    public refreshQuestions() {
        this.getQuestionsByCourseIdAsync(this.course?.value?.id, this.questionFilter?.value);
    }

    public getUserCourseReviews(): Observable<ReviewTO[]> {
        if (!this.courseSubscription?.value?.id) return;

        this.loading.next(true);
        return this.courseSubscriptionService
            .GetProgressionByCourseSubscription(this.courseSubscription?.value?.id)
            .pipe(
                mergeMap((progression) => {
                    this.setCurrentDataByProgression(progression);
                    return this.courseService.getCourseReviews(
                        this.course.value.id,
                        0,
                        10,
                        null,
                        this.authService.getClaimsUserId()
                    );
                }),
                tap(() => {
                    this.loading.next(false);
                    this.loadingInfo.next("GETTING_COURSE");
                }),
                catchError((error) => {
                    this.loading.next(false);
                    return throwError(error);
                })
            );
    }

    public getQuestionsByCourseIdAsync(courseId: string, searchTitle: string = null): void {
        this.loading.next(true);
        const query: QuestionQueryParametersDTO = {
            CursoId: courseId,
            find: searchTitle,
            skip: (this.getQuestionPageNumber() - 1) * 10,
            take: 10,
            filterBy: this.getQuestionsFilter(),
            orderBy: this.getQuestionsOrder(),
            instructorId: null,
            fillCourse: true
        };
        this.courseService.ListQuestions(query).subscribe(
            (questions) => {
                if (!questions || questions.length === 0) {
                    this.hasMoreQuestions.next(false);
                } else {
                    const userId = this.authService.getClaimsUserId();
                    questions.forEach((q) => {
                        q.isLiked = q.curtidas?.findIndex((c) => c.userId === userId) >= 0;
                        q.isBookmarked = q.favoritas?.findIndex((c) => c.userId === userId) >= 0;
                    });

                    let allQuestions = [];
                    const currentQuestions = this.questions.value;

                    const search = searchTitle !== undefined && searchTitle !== null && searchTitle !== "";

                    if (currentQuestions && !search) {
                        allQuestions = [
                            ...currentQuestions.filter((a) => questions.find((b) => b.id == a.id) == null),
                            ...questions
                        ];
                    } else {
                        allQuestions = [...questions];
                    }

                    this.questions.next(allQuestions);
                    this.hasMoreQuestions.next(questions.length >= 10);
                }
            },
            (error) => {
                this.hasMoreQuestions.next(false);
                this.snackBar.showHttpMessage(error.status, error.message);
            },
            () => {
                this.loading.next(false);
            }
        );
    }

    public getQuestionsAsync = (): Observable<PerguntaTO[]> => this.questions.asObservable();

    public getQuestionsFilterAsync = (): Observable<QuestionFilter> => this.questionFilter.asObservable();

    private getQuestionsFilter = (): QuestionFilter => this.questionFilter.value;

    public getQuestionsOrderAsync = (): Observable<QuestionOrder> => this.questionOrder.asObservable();

    private getQuestionsOrder = (): QuestionOrder => this.questionOrder.value;

    private getQuestionPageNumber = (): number => this.questionPageNumber.value;

    public getHasMoreQuestionsAsync = (): Observable<boolean> => this.hasMoreQuestions.asObservable();

    public BookmarkQuestion(questionId: string): void {
        this.toggleBookmark(questionId);

        this.apiUserService.bookmarkQuestion(this.course.value.id, questionId).subscribe(
            () => {},
            (error) => {
                this.toggleBookmark(questionId);
                this.snackBar.showHttpMessage(error.status, error.message);
            }
        );
    }

    private toggleBookmark(questionId: string) {
        const itemKey = `bookmarked_${questionId}`;

        const question = this.questions.value.find((q) => q.id === questionId);
        const idx = this.questions.value.indexOf(question);

        const tempArray = this.questions.value;

        if (question.isBookmarked) {
            tempArray[idx].isBookmarked = false;
            tempArray[idx].quantidadeFavoritas -= 1;
        } else {
            tempArray[idx].isBookmarked = true;
            tempArray[idx].quantidadeFavoritas += 1;
        }

        localStorage.setItem(itemKey, `${tempArray[idx].isBookmarked}`);

        this.questions.next(tempArray);
    }

    public LikeQuestion(questionId: string): void {
        this.toggleLike(questionId);

        this.apiUserService.likeQuestion(this.course.value.id, questionId).subscribe(
            () => {},
            (error) => {
                this.toggleLike(questionId);
                this.snackBar.showHttpMessage(error.status, error.message);
            }
        );
    }

    private toggleLike(questionId: string) {
        const itemKey = `liked_${questionId}`;

        const question = this.questions.value.find((q) => q.id === questionId);
        const idx = this.questions.value.indexOf(question);

        const tempArray = this.questions.value;

        if (question.isLiked) {
            tempArray[idx].isLiked = false;
            tempArray[idx].quantidadeCurtidas -= 1;
        } else {
            tempArray[idx].isLiked = true;
            tempArray[idx].quantidadeCurtidas += 1;
        }

        localStorage.setItem(itemKey, `${tempArray[idx].isLiked}`);

        this.questions.next(tempArray);
    }

    public ClearQuestionFilter(): void {
        this.questionFilter.next(undefined);
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);

        this.getQuestionsByCourseIdAsync(this.course.value.id);
    }

    public ClearQuestionOrder(): void {
        this.questionOrder.next(undefined);
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);

        this.getQuestionsByCourseIdAsync(this.course.value.id);
    }

    public SetQuestionFilter(filterBy: QuestionFilter): void {
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);
        this.questions.next(undefined);

        this.questionFilter.next(filterBy);
        this.getQuestionsByCourseIdAsync(this.course.value.id);
    }

    public SetQuestionOrder(orderBy: QuestionOrder): void {
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);
        this.questions.next(undefined);

        this.questionOrder.next(orderBy);
        this.getQuestionsByCourseIdAsync(this.course.value.id);
    }

    public LoadMoreQuestions(): void {
        const pageNumber = this.getQuestionPageNumber() + 1;
        this.questionPageNumber.next(pageNumber);

        this.getQuestionsByCourseIdAsync(this.course.value.id);
    }

    public clearQuestionParams(): void {
        this.questionFilter.next(undefined);
        this.questionPageNumber.next(1);
        this.hasMoreQuestions.next(true);
        this.questions.next(undefined);
    }

    public getTotalLessons = (): number => {
        const currModulePos = this.getCourseModules();
        let lessonCounter = 0;
        for (let i = 0; i < currModulePos.length; i++) {
            lessonCounter += this.getModuleLessons(currModulePos[i])?.length;
        }
        return lessonCounter;
    };

    public getCourseModules = (): Array<ModuloTO> => {
        return (this.course.getValue()?.modulos ?? [])
            .filter((m) => m.habilitado)
            .sort((a, b) => a.ordem - b.ordem || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    };

    public getModuleLessons = (modulo: ModuloTO): Array<AulaTO> => {
        return (modulo.aulas ?? [])
            .filter((l) => l.habilitado)
            .sort((a, b) => a.ordem - b.ordem || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    };

    public getCourseDurationInMilliseconds = (): number => {
        if (this.course?.value?.totalCourseTime) return this.course.value.totalCourseTime;
        return this.getCourseModules()
            ?.map((modulo) => this.getModuleDurationInMilliseconds(modulo))
            .reduce((a, b) => a + b, 0);
    };

    public getModuleDurationInMilliseconds = (modulo: ModuloTO): number => {
        return this.getModuleLessons(modulo)
            ?.map((aula) => aula.duracao)
            .reduce((a, b) => a + b, 0);
    };

    public fetchCoursesForRevision(): void {
        this.loading.next(true);

        this.courseService.getCoursesForRevision().subscribe(
            (coursesForRevision) => {
                this.loading.next(false);
                this.coursesForRevision.next(coursesForRevision);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public getCoursesForRevisionAsync = (): Observable<CourseSummaryDTO[]> => this.coursesForRevision.asObservable();

    public publishCourse(id: string): void {
        this.loading.next(true);

        this.courseService.publishCourse(id).subscribe(
            (success) => {
                this.loading.next(false);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public blockCourse(id: string, reason: string): void {
        this.loading.next(true);

        this.courseService.blockCourse(id, reason).subscribe(
            (success) => {
                this.loading.next(false);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public courseNeedReviewAsync = (): Observable<boolean> => this.courseNeedReview.asObservable();

    setLoading(loading: boolean): void {
        this.loading.next(loading);
    }

    checkCanLoadCourse(courseId: string, userId: string): Observable<boolean> {
        console.log("[checkCanLoadCourse] Start - Get course status");
        this.loadingInfo.next("GETTING_COURSE");

        return this.courseService.getCourseById(courseId).pipe(
            switchMap((course) => {
                if (!course.habilitado) {
                    return throwError({ message: "COURSE_NOT_AVAILABLE", type: "UNAVAILABLE" });
                }

                console.log("[checkCanLoadCourse] Start - Get course subscription");
                this.loadingInfo.next("GETTING_COURSE_SUBSCRIPTION");
                return this.courseSubscriptionService.GetList({
                    count: false,
                    orderBy: "createdat",
                    sortOrder: "desc",
                    active: true,
                    courseId: courseId,
                    userId: userId,
                    skip: 0,
                    take: 1
                });
            }),
            switchMap((res: any) => {
                const courseSubscription = res?.data[0];
                this.courseSubscription.next(courseSubscription);

                if (courseSubscription?.expirationAt) {
                    // check if the course subscription has expired
                    const now = new Date();
                    const expirationDate = new Date(courseSubscription.expirationAt);
                    if (expirationDate < now) {
                        // if expired return cant load course
                        console.log("[checkCanLoadCourse] Course subscription expired");
                        return of(false);
                    }
                } else if (!courseSubscription) {
                    //if hasn't course subscription try subscribe
                    console.log("[checkCanLoadCourse] Try subscribe into course...");
                    this.loadingInfo.next("SUBSCRIBING_INTO_COURSE");
                    return this.userCourseService.RegisterUserIntoCourse(userId, courseId).pipe(
                        tap((courseSubscription) => {
                            console.log("[checkCanLoadCourse] Subscribed into course");
                            this.courseSubscription.next(courseSubscription);
                            localStorage.setItem(`course_subscribed_${userId}_${courseId}`, courseId);

                            this.dataAnalysisService.freeCourseRegistration({ id: courseId } as any, this.user);
                        }),
                        map(() => true)
                    );
                }

                return of(true);
            }),
            catchError((error) => {
                if (error.type === "UNAVAILABLE") {
                    return throwError(error);
                }
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loadingInfo.next(undefined);
                return throwError(error);
            })
        );
    }
}
